import type { ColPresetType } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import type { DashboardWidgetType } from '../../dashboard/Dashboard.types';
import { isRecord, toPresetsByPage, toWidgetList } from './configValues';
import type {
	AttachMode,
	ColumnTemplate,
	DashboardTemplate,
	PresetsByPage,
} from './types';

export const newId = (): string =>
	`${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

/**
 * Copies get new ids: widgets keep per-id local state (auto refresh, scale
 * locks), and two copies of one template must not collide. They are marked
 * as assigned, so the user cannot change their parameters.
 */
export const applyDashboardTemplate = (
	existing: DashboardWidgetType[],
	template: DashboardTemplate,
	mode: AttachMode,
): DashboardWidgetType[] => {
	const copies = template.widgets.map((w) => ({
		...w,
		id: newId(),
		assignedByAdmin: true,
	}));
	if (mode === 'replace') return copies;
	const offset = existing.reduce(
		(max, w) => Math.max(max, (w.y ?? 0) + (w.h ?? 2)),
		0,
	);
	return [
		...existing,
		...copies.map((w) => ({ ...w, y: (w.y ?? 0) + offset })),
	];
};

export const applyColumnTemplate = (
	existing: ColPresetType[],
	template: ColumnTemplate,
	mode: AttachMode,
): ColPresetType[] => {
	const preset = template.preset;
	if (mode === 'replace') return [preset];
	const index = existing.findIndex((p) => p.name === preset.name);
	if (index === -1) return [...existing, preset];
	return existing.map((p, i) => (i === index ? preset : p));
};

/** Next value of a user's `dashboardWidgets` row after attaching. */
export const attachDashboardValue = (
	current: unknown,
	template: DashboardTemplate,
	mode: AttachMode,
): DashboardWidgetType[] =>
	applyDashboardTemplate(toWidgetList(current) ?? [], template, mode);

/** Next value of a user's `columns` row; keys other than the page are kept as stored. */
export const attachColumnsValue = (
	current: unknown,
	template: ColumnTemplate,
	mode: AttachMode,
): PresetsByPage => {
	const byPage = toPresetsByPage(current) ?? {};
	return {
		...(isRecord(current) ? (current as PresetsByPage) : {}),
		[template.page]: applyColumnTemplate(
			byPage[template.page] ?? [],
			template,
			mode,
		),
	};
};
